import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { ApiResponse } from "@apiModels/ApiResponse.csharp";
import { GenericModal } from "../../Components/BookingLegacy/FormElements/PopUps/GenericModal";
import classnames from "classnames";
import { contactFormDataValidationSchema } from "./ContactFormDataValidation";
import { LoadingAnimation } from "../../Components/LoadingAnimation/LoadingAnimation";
import { FormInput } from "../../Components/Form/FormInput";
import { ContactFormProps } from "./ContactFormProps.csharp";
import { FormSelect } from "../../Components/Form/FormSelect";
import { ContactFormApi } from "../../Components/Api/ContactFormApi";

interface ContactFormData {
  name?: string;
  phoneNumber?: string;
  optionDisplayName?: string;
  subOptionDisplayName?: string;
}

export const ContactForm: React.FC<ContactFormProps> = ({
  namePlaceholder,
  phoneNumberPlaceholder,
  inputRequiredValidation,
  phoneValidation,
  submitButtonLabel,
  successModalDescription,
  successModalTitle,
  failedSentDescription,
  blockContentLinkId,
  centerPlaceholder,
  centerSubPlaceholder,
  contactOptions,
  displayPageAfterSubmissionUrl,
}) => {
  const [formSubmissionResult, setFormSubmissionResult] =
    useState<ApiResponse>();
  const [submitInProgress, setSubmitInProgress] = useState(false);
  
  const defaultSuccessTitle = "Takk!";
  const defaultSuccessDescription = "Din forespørsel er nå sendt";
  

  const {
    reset,
    register,
      watch,
    formState: { errors },
    handleSubmit,
  } = useForm<ContactFormData>({
    resolver: yupResolver(
      contactFormDataValidationSchema({
        required: inputRequiredValidation,
        phone: phoneValidation,
      }, contactOptions)
    ),
    defaultValues: {
      optionDisplayName:
        contactOptions && contactOptions.length == 1
          ? contactOptions[0].displayName
          : undefined
    },
  });

  const selectedOption = watch('optionDisplayName');
  const selectedSubOptions = contactOptions.find(
      option => option.displayName === selectedOption
  )?.contactOptions || [];
  
  const contactFormApi = new ContactFormApi();

  const onSubmit = async (data: ContactFormData) => {
    setSubmitInProgress(true);
    try {
      const result = await contactFormApi.contactForm({
        optionDisplayName: data.optionDisplayName!,
        subOptionDisplayName: data.subOptionDisplayName,
        name: data.name!,
        phoneNumber: data.phoneNumber!,
        blockContentLinkId: blockContentLinkId.toString(),
      });

      if (displayPageAfterSubmissionUrl) {
        window.location.href = displayPageAfterSubmissionUrl;
      } else {
        setFormSubmissionResult(result);
      }
    } catch (error) {
      setFormSubmissionResult({ isSuccess: false, messages: [] });
    }
    setSubmitInProgress(false);
  };

  return (
    <div>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="EPiServerForms"
        encType="multipart/form-data"
        name="ContactForm"
      >
        <FormInput
          registerReturn={register("name")}
          placeholder={namePlaceholder}
          error={errors.name}
          type="textbox"
          disabled={submitInProgress}
        />
        <FormInput
          registerReturn={register("phoneNumber")}
          placeholder={phoneNumberPlaceholder}
          error={errors.phoneNumber}
          type="number"
          disabled={submitInProgress}
        />
        {contactOptions && contactOptions.length > 1 && (
          <FormSelect
            placeholder={centerPlaceholder}
            registerReturn={register("optionDisplayName")}
            error={errors.optionDisplayName}
            disabled={submitInProgress}
            options={contactOptions.map((c) => ({
              text: c.displayName,
              value: c.displayName,
            }))}
          />
        )}
        {selectedSubOptions.length > 0 && (
          <FormSelect
            placeholder={centerSubPlaceholder}
            registerReturn={register("subOptionDisplayName")}
            error={errors.subOptionDisplayName}
            disabled={submitInProgress}
            options={selectedSubOptions.map((c) => ({
              text: c.displayName,
              value: c.displayName,
            }))}
          />
        )}

        <button
          type="submit"
          disabled={submitInProgress}
          className={classnames(
            "FormSubmitButton",
            "FormSubmitButtonWithValidation"
          )}
        >
          {submitInProgress ? (
            <LoadingAnimation text={submitButtonLabel} />
          ) : (
            submitButtonLabel
          )}
        </button>
        {formSubmissionResult && !formSubmissionResult.isSuccess && (
          <span className="Form__Element__ValidationError">
            {failedSentDescription}
          </span>
        )}

        <GenericModal
          onClose={() => {
            reset();
            setFormSubmissionResult(undefined);
          }}
          isOpen={formSubmissionResult?.isSuccess ?? false}
          modalHeader={successModalTitle || defaultSuccessTitle}
          modalDescription={successModalDescription || defaultSuccessDescription}
        />
      </form>
    </div>
  );
};
